import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { FormController } from '@adornis/forms/x-form-controller.js';
import { routingState } from '@adornis/router/client/routing-state.js';
import type { ValidationError } from '@adornis/validation/errors/ValidationError.js';
import { CampusExercise } from '@chancenwerk/campus-learning-content/entities/CampusExercise.js';
import { CampusLesson } from '@chancenwerk/campus-learning-content/entities/CampusLesson.js';
import { CrPage } from '@chancenwerk/design/components/cr-page.js';
import { html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { of, switchMap, takeUntil } from 'rxjs';

/**
 * @customElement campus-page-exercise-edit
 */
@customElement('campus-page-exercise-edit')
export class CampusComponentExerciseEdit extends CrPage {
  public static exerciseType = -1;
  public static exerciseName = 'Aufgabe';
  public static exerciseElement = 'campus-page-exercise-edit';

  @state() paramExerciseID = new RXController(this, routingState.searchParams.get('exerciseID'));
  @state() paramLessonID = new RXController(this, routingState.searchParams.get('lessonID'));
  @state() paramCloseOnSave = new RXController(this, routingState.searchParams.get('closeOnSave'));

  @state() exercise = new RXController(
    this,
    this.paramExerciseID.observable.pipe(
      switchMap(id => {
        if (!id || id === '') return of(new CampusExercise({}));
        return CampusExercise.subscribeByID<CampusExercise>(id)(CampusExercise.allFields);
      }),
    ),
  );
  @state() lesson = new RXController(
    this,
    this.paramLessonID.observable.pipe(
      switchMap(id => {
        if (!id || id === '') return of(new CampusLesson({}));
        return CampusLesson.subscribeByID<CampusLesson>(id)(CampusLesson.allFields);
      }),
    ),
  );

  @state() protected readonly errorMsg = new RXController(this, '');
  @state() protected readonly form = new FormController(this, this.exercise.observable, {
    success: exercise => this.handleFormSuccess(exercise),
    error: err => this.handleFormError(err),
  });

  override connectedCallback() {
    super.connectedCallback();

    this.exercise.observable
      .pipe(takeUntil(this.disconnected))
      .subscribe(
        e => (this.label = !e?._id ? this.getExcersieName() + ' erstellen' : this.getExcersieName() + ' bearbeiten'),
      );
  }

  override header() {
    return html` <cr-flex horizontal space="md" end>
      <cr-button @click=${() => this.form.validate()}>Speichern</cr-button>
    </cr-flex>`;
  }

  protected getExcersiseType() {
    return CampusComponentExerciseEdit.exerciseType;
  }

  protected getExcersieName() {
    return CampusComponentExerciseEdit.exerciseName;
  }

  protected async handleFormSuccess(exercise: CampusExercise) {
    if (!this.lesson.value) {
      return;
    }

    if (exercise.title === '') return this.errorMsg.next('Bitte einen Titel eingeben!');

    exercise.type = this.getExcersiseType();
    if (!exercise._id) {
      await exercise.create().catch(e => this.errorMsg.next(e.detail));
      this.lesson.value.exerciseIDs.push(exercise._id);
      if (this.lesson.value._id) await this.lesson.value.save();
    } else await exercise.save().catch(e => this.errorMsg.next(e.detail));

    this.exercise.next(exercise);

    if (this.paramCloseOnSave.value) window.close();
  }

  protected async handleFormError(err: ValidationError<any>) {
    this.errorMsg.next(err.message);
  }
}
